import { useState, useEffect } from 'react';
import {
  type Data,
  DropDown,
  type DropDownDataInterface,
  FAIcon,
  Input,
  MLPVersion,
  TextArea,
  Toggle,
  useSession,
  useTranslation,
} from '@nextgen-web-framework/all';
import {
  CreateListRequest,
  ListType,
  checkShareableListType,
  getCategoryIdByCategoryName,
  getCategoryNameByCategoryId,
  listCategoryDataTransformer,
} from '@commerce-cart-webcomponents/sdk';
import { useFetchCmsContent } from '../../../../common';
import styles from './CreateList.module.scss';
import { type CreateListProps } from './types/propsType';

export const CreateList = ({ listDetails, onListDataChange }: CreateListProps) => {
  const { isABO } = useSession();
  const t = useTranslation();
  const [listCategories, setListCategories] = useState<Data[]>([]);

  const [enableSharing, setEnableSharing] = useState<boolean>(listDetails ? checkShareableListType(listDetails) : true);
  const categoryData = useFetchCmsContent();

  const [listData, setListData] = useState<CreateListRequest>(
    (listDetails as CreateListRequest) ?? {
      entries: [],
      name: '',
      description: '',
      listType: isABO() ? ListType.Shareable : ListType.Private,
      category: '',
    },
  );

  const updateList = (key: keyof CreateListRequest, value: string) => {
    const updatedList = {
      ...listData,
      [key]: value,
    };
    setListData(updatedList);
    onListDataChange?.(updatedList);
  };

  const toggleHandler = () => {
    setEnableSharing(!enableSharing);
    updateList('listType', !enableSharing ? ListType.Shareable : ListType.Private);
  };

  useEffect(() => {
    const obj: Data[] = [];
    listCategoryDataTransformer(categoryData).forEach((cat) => {
      obj.push({
        category: cat.category_name,
        label: (
          <div className={styles['category']}>
            <span
              className={styles['category-color-block']}
              style={{
                backgroundColor: cat.background_color,
              }}
            ></span>
            {cat.category_name}
          </div>
        ),
      });
    });
    setListCategories(obj);
  }, [categoryData]);

  const categoryName = getCategoryNameByCategoryId(listData?.category || '', listCategoryDataTransformer(categoryData));

  return (
    <div className={`${styles['create-list-container']}`}>
      <Input
        type="text"
        label={t('cart.createList.listName')}
        mlpVersion={MLPVersion.TWO}
        name="name"
        min={3}
        maxLength={52}
        placeholder={t('cart.createList.listName')}
        value={listData.name}
        data-testid="name"
        onChange={(e) => updateList('name', e.target.value)}
      />
      <TextArea
        id="textarea"
        label={t('cart.createList.listDescription')}
        mlpVersion={MLPVersion.TWO}
        value={listData.description}
        data-testid="description"
        minLength={3}
        maxLength={256}
        onChange={(e) => updateList('description', e.target.value)}
      />
      <DropDown
        configData={{
          label: 'label',
          onSelect: (e: DropDownDataInterface) => {
            const categoryId = getCategoryIdByCategoryName(
              String(e.category),
              listCategoryDataTransformer(categoryData),
            );
            updateList('category', categoryId as string);
          },
          disableItemKey: 'isDisabled',
        }}
        dropdownLabel={t('cart.createList.listTheme')}
        value={categoryName}
        data={listCategories}
        mlpVersion={MLPVersion.TWO}
        name="theme"
        data-testId="category"
        disabledFilter={true}
        // Commenting this as we dont need to change input here we just need selection from provided list
        // onChange={(e) => updateList('category', e.target.value)}
      />
      {isABO() && (
        <div className={`${styles['enable-sharing']}`}>
          <div className={`${styles['enable-sharing-container']}`}>
            <span className={`${styles['enable-sharing-container-title']}`}>{t('cart.createList.enableSharing')}</span>

            <div className={`${styles['enable-sharing-container-subtitle']}`}>
              <FAIcon
                iconName={enableSharing ? 'eye' : 'eye-slash'}
                className={`${styles['enable-sharing-container-subtitle-eyeIcon']}`}
              />
              {enableSharing ? t('cart.createList.anyoneCanViewList') : t('cart.createList.onlyYouCanViewList')}
            </div>
          </div>

          <div className={`${styles['enable-sharing-toggleIcon']}`}>
            <Toggle onClick={toggleHandler} checked={enableSharing} />
          </div>
        </div>
      )}
    </div>
  );
};
